/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  adminActivityActions,
  adminActivityAnalytics,
  adminDeleteUser,
  adminGetUser,
  adminInviteUser,
  adminListActivity,
  adminListUsers,
  adminUpdateUser,
  changePassword,
  getActivity,
  getHealth,
  getMe,
  getMeDetail,
  login,
  logout,
  signup,
  updateProfile,
} from "@/lib/api/generated";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; rememberMe?: boolean }) => {
      const { data } = await login({
        body: {
          email: vars.email,
          password: vars.password,
          remember_me: vars.rememberMe ?? false,
        },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
    }) => {
      const { data } = await signup({
        body: {
          email: vars.email,
          password: vars.password,
          first_name: vars.firstName,
          last_name: vars.lastName,
        },
      });
      return data;
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { currentPassword: string; newPassword: string }) => {
      await changePassword({
        body: { current_password: vars.currentPassword, new_password: vars.newPassword },
      });
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}
